import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { PERSONAL_EXPENSES_KEY } from './constants';
import { getPersonalExpense } from './personalExpenses';
import { FormOptionPersonalExpense } from './personalExpenses.types';
import { restOptionToFormOption } from '@/api/REST/utils';
import { formatCustomerDisplayName } from '@/api/typesense/customers';
import { formatEmployeeDisplayName } from '@/api/typesense/employees';
import { formatProjectDisplayName } from '@/api/typesense/projects';

export const useGetPersonalExpense = (id: number) => {
  const { data, ...restProps } = useQuery({
    queryKey: [PERSONAL_EXPENSES_KEY, id],
    queryFn: () => getPersonalExpense(id),
    enabled: !!id,
  });

  const formOptionMassagedData: FormOptionPersonalExpense | undefined = useMemo(
    () =>
      !data
        ? undefined
        : {
            ...data,
            project: restOptionToFormOption(data.project, (p) =>
              formatProjectDisplayName(p.name, p.number),
            ),
            customer: restOptionToFormOption(data.customer, (c) =>
              formatCustomerDisplayName(c.name, c.number),
            ),
            currency: {
              id: data.currencyId.toString(),
              value: data.currency.currencyCode,
              isoCode: data.currency.currencyCode,
            },
            expenseType: restOptionToFormOption(data.expenseType),
            employee: restOptionToFormOption(
              {
                ...data.employee,
                id: data.employee.userId, // to align with Typesense, the userId is used as the id (SourceId in Typesense)
              },
              (e) => formatEmployeeDisplayName(e.name, e.initials),
            ),
            paymentMethod: restOptionToFormOption(data.paymentMethod),
            projectSubContract: restOptionToFormOption(data.projectSubContract),
            supplier: data.supplier
              ? { id: data.supplier.id.toString(), value: data.supplier.name }
              : undefined,
          },
    [data],
  );

  return { data: formOptionMassagedData, ...restProps };
};
